import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Build as BuildIcon,
  Home as HomeIcon,
  AdminPanelSettings as AdminIcon,
  Notifications as NotificationsIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
  HelpOutline as HelpIcon,
  Feedback as FeedbackIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import TutorialModal from './Tutorial/TutorialModal';
import { hasSeenTutorial } from './Tutorial/tutorialContent';
import FeedbackDialog from './FeedbackDialog';

const Navigation = ({
  onNotificationsClick,
  unreadCount = 0,
  showNotifications = true,
  showLogout = true,
  showHelp = true,
}) => {
  const navigate = useNavigate();
  const { logout, effectiveRole, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialRole, setTutorialRole] = useState(null);
  const [seenTick, setSeenTick] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const activeRole = effectiveRole || user?.role || null;
  const showsHelp = showHelp;
  const helpUnseen = activeRole ? !hasSeenTutorial(activeRole) : false;

  // Re-check storage on tutorial close
  useEffect(() => {
    if (!tutorialOpen) setSeenTick((t) => t + 1);
    // seenTick is intentionally used as a re-render trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialOpen]);

  const openTutorial = () => {
    setTutorialRole(activeRole);
    setTutorialOpen(true);
    setMobileMenuOpen(false);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const helpButton = (
    <Tooltip title="How it works">
      <IconButton
        onClick={openTutorial}
        sx={{ color: 'primary.main' }}
        aria-label="How Congrow works"
      >
        <Badge
          variant="dot"
          color="error"
          invisible={!helpUnseen}
          overlap="circular"
          key={seenTick}
        >
          <HelpIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  );

  const feedbackButton = (
    <Tooltip title="Send feedback">
      <IconButton
        onClick={() => { setFeedbackOpen(true); setMobileMenuOpen(false); }}
        sx={{ color: 'primary.main' }}
        aria-label="Send feedback"
      >
        <FeedbackIcon />
      </IconButton>
    </Tooltip>
  );

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 250,
          pt: 2,
        },
      }}
    >
      <List>
        {showsHelp && (
          <ListItem button onClick={openTutorial}>
            <ListItemIcon>
              <Badge variant="dot" color="error" invisible={!helpUnseen} overlap="circular">
                <HelpIcon color="primary" />
              </Badge>
            </ListItemIcon>
            <ListItemText primary="How it works" />
          </ListItem>
        )}
        <ListItem button onClick={() => handleMenuItemClick('/about')}>
          <ListItemIcon>
            <InfoIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="About Us" />
        </ListItem>
        <ListItem button onClick={() => handleMenuItemClick('/contact')}>
          <ListItemIcon>
            <ContactIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Contact" />
        </ListItem>
        <ListItem button onClick={() => handleMenuItemClick('/login?role=contractor')}>
          <ListItemIcon>
            <BuildIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Contractor" />
        </ListItem>
        <ListItem button onClick={() => handleMenuItemClick('/login?role=homeowner')}>
          <ListItemIcon>
            <HomeIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Homeowner" />
        </ListItem>
        <ListItem button onClick={() => handleMenuItemClick('/login?role=admin')}>
          <ListItemIcon>
            <AdminIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Admin" />
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 2 }}>
        <Toolbar>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/logo.png"
              alt="Congrow Logo"
              style={{
                height: isMobile ? '40px' : '60px',
                marginRight: isMobile ? '8px' : '16px',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                color="primary"
                onClick={() => navigate('/about')}
              >
                About Us
              </Button>
              <Button
                color="primary"
                onClick={() => navigate('/contact')}
              >
                Contact
              </Button>
              <Button
                color="primary"
                startIcon={<BuildIcon />}
                onClick={() => navigate('/login?role=contractor')}
              >
                Contractor
              </Button>
              <Button
                color="primary"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/login?role=homeowner')}
              >
                Homeowner
              </Button>
              <Button
                color="primary"
                startIcon={<AdminIcon />}
                onClick={() => navigate('/login?role=admin')}
              >
                Admin
              </Button>

              {showsHelp && helpButton}
              {showsHelp && user && feedbackButton}

              {/* Notifications Icon */}
              {showNotifications && (
                <IconButton onClick={onNotificationsClick} sx={{ color: 'primary.main' }} aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}>
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              )}

              {/* Logout Icon */}
              {showLogout && (
                <IconButton onClick={logout} sx={{ color: 'primary.main' }} aria-label="Log out">
                  <LogoutIcon />
                </IconButton>
              )}
            </Box>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showsHelp && helpButton}
              {showsHelp && user && feedbackButton}

              {/* Notifications Icon */}
              {showNotifications && (
                <IconButton onClick={onNotificationsClick} sx={{ color: 'primary.main' }} aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}>
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              )}

              {/* Logout Icon */}
              {showLogout && (
                <IconButton onClick={logout} sx={{ color: 'primary.main' }} aria-label="Log out">
                  <LogoutIcon />
                </IconButton>
              )}

              {/* Hamburger Menu */}
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: 'primary.main' }}
                aria-label="Open menu"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      {mobileMenu}

      <TutorialModal
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        initialRole={tutorialRole}
      />

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
      />
    </>
  );
};

export default Navigation;
